import React, { useCallback, useEffect, useState } from "react";
import { CalendarDays, Edit3, RefreshCw, RotateCcw } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

function AdminBidRevert() {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [editBet, setEditBet] = useState(null);

  const loadBets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedDate) params.set("date", selectedDate);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const res = await API.get(`/admin/bets?${params.toString()}`);
      setBets(Array.isArray(res.data) ? res.data : []);
    } catch {
      setBets([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, statusFilter]);

  useEffect(() => {
    loadBets();
  }, [loadBets]);

  const revertBet = async (id) => {
    try {
      await API.post(`/admin/bets/${id}/revert`);
      await loadBets();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to revert bid");
    }
  };

  const openEdit = (bet) => {
    setEditBet({
      id: bet.id,
      number: String(bet.number || ""),
      amount: String(Number(bet.amount || 0)),
      label: `${bet.user_name || `User ${bet.user_id}`} / ${bet.market_name || "-"}`,
    });
  };

  const saveEdit = async () => {
    if (!editBet?.id) return;
    try {
      await API.put(`/admin/bets/${editBet.id}`, {
        number: editBet.number,
        amount: Number(editBet.amount || 0),
      });
      setEditBet(null);
      await loadBets();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to edit bet");
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>Bid Revert</h1>
            <p style={subtitle}>Pending bids only. Reverting returns points to the user wallet.</p>
          </div>
          <button type="button" style={refreshButton} onClick={loadBets}>
            <RefreshCw size={16} /> {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <section style={filterBar}>
          {["all", "pending", "won", "lost", "reverted"].map((status) => (
            <button
              key={status}
              type="button"
              style={statusFilter === status ? activeFilterButton : filterButton}
              onClick={() => setStatusFilter(status)}
            >
              {prettyType(status)}
            </button>
          ))}
          <label style={dateFilterBox}>
            <CalendarDays size={15} />
            <input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value || getTodayDate())} style={dateInput} />
          </label>
        </section>

        <section style={tableCard}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>ID</th>
                <th style={th}>User</th>
                <th style={th}>Market</th>
                <th style={th}>Type</th>
                <th style={th}>Number</th>
                <th style={th}>Amount</th>
                <th style={th}>Status</th>
                <th style={th}>Date</th>
                <th style={{ ...th, textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {bets.map((bet) => (
                <tr key={bet.id}>
                  <td style={td}>{bet.id}</td>
                  <td style={td}>
                    <strong style={userName}>{bet.user_name || `User ${bet.user_id}`}</strong>
                    <span style={userPhone}>{bet.user_phone || "-"}</span>
                  </td>
                  <td style={td}>{bet.market_name || "-"}</td>
                  <td style={td}>{prettyType(bet.type)}</td>
                  <td style={td}>{bet.number}</td>
                  <td style={td}><strong>Rs {Number(bet.amount || 0)}</strong></td>
                  <td style={td}><span style={statusPill(bet.status)}>{bet.status || "pending"}</span></td>
                  <td style={td}>{formatDateTime(bet.date || bet.created_at)}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <div style={actionRow}>
                      <button type="button" style={editButton} onClick={() => openEdit(bet)}>
                        <Edit3 size={15} /> Edit
                      </button>
                      <button type="button" style={revertButton} onClick={() => revertBet(bet.id)} disabled={String(bet.status || "pending").toLowerCase() !== "pending"}>
                        <RotateCcw size={15} /> Revert
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!bets.length && (
                <tr>
                  <td style={emptyCell} colSpan={9}>No bids found</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {editBet && (
          <div style={overlay}>
            <section style={modal}>
              <div style={modalHeader}>
                <strong>Edit Bet #{editBet.id}</strong>
                <button type="button" style={closeButton} onClick={() => setEditBet(null)}>x</button>
              </div>
              <p style={modalMeta}>{editBet.label}</p>
              <label style={field}>
                <span>Bet Number</span>
                <input style={input} value={editBet.number} onChange={(e) => setEditBet((current) => ({ ...current, number: e.target.value.replace(/[^\d-]/g, "") }))} />
              </label>
              <label style={field}>
                <span>Amount</span>
                <input style={input} value={editBet.amount} inputMode="decimal" onChange={(e) => setEditBet((current) => ({ ...current, amount: e.target.value.replace(/[^\d.]/g, "") }))} />
              </label>
              <p style={warningText}>Submit time stays unchanged. Wallet difference is adjusted automatically.</p>
              <div style={modalFooter}>
                <button type="button" style={cancelButton} onClick={() => setEditBet(null)}>Cancel</button>
                <button type="button" style={saveButton} onClick={saveEdit}>Save Bet</button>
              </div>
            </section>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}

function prettyType(value = "") {
  return String(value || "-").replace(/[-_]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}, ${date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
}

function getTodayDate() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
  return `${map.year}-${map.month}-${map.day}`;
}

const page = { display: "grid", gap: "18px" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const title = { margin: 0, fontSize: "22px", fontWeight: 800 };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: "13px" };
const refreshButton = { height: "44px", padding: "0 16px", border: "1px solid #dbe4f0", background: "#fff", borderRadius: "12px", display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, cursor: "pointer" };
const filterBar = { display: "flex", gap: "8px", flexWrap: "wrap" };
const filterButton = { height: "36px", border: "1px solid #dbe4f0", background: "#fff", borderRadius: "999px", padding: "0 14px", color: "#475569", fontWeight: 800, cursor: "pointer" };
const activeFilterButton = { ...filterButton, background: "#eff6ff", borderColor: "#bfdbfe", color: "#2563eb" };
const dateFilterBox = { height: "36px", border: "1px solid #bfdbfe", background: "#eff6ff", borderRadius: "999px", padding: "0 12px", color: "#2563eb", display: "inline-flex", alignItems: "center", gap: "7px", fontWeight: 800 };
const dateInput = { width: "134px", border: 0, outline: 0, background: "transparent", color: "#1d4ed8", fontWeight: 800, fontSize: "13px" };
const tableCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", overflow: "auto" };
const table = { width: "100%", minWidth: "1080px", borderCollapse: "collapse" };
const th = { height: "48px", padding: "0 18px", textAlign: "left", color: "#64748b", fontSize: "11px", textTransform: "uppercase", borderBottom: "1px solid #e5e7eb" };
const td = { padding: "15px 18px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", fontSize: "13px" };
const userName = { display: "block", fontSize: "14px", fontWeight: 800 };
const userPhone = { display: "block", marginTop: "3px", color: "#64748b", fontSize: "12px" };
const actionRow = { display: "inline-flex", gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" };
const editButton = { height: "36px", padding: "0 12px", border: 0, borderRadius: "10px", background: "#eff6ff", color: "#2563eb", display: "inline-flex", alignItems: "center", gap: "7px", fontWeight: 800, cursor: "pointer" };
const revertButton = { height: "36px", padding: "0 12px", border: 0, borderRadius: "10px", background: "#fee2e2", color: "#dc2626", display: "inline-flex", alignItems: "center", gap: "7px", fontWeight: 800, cursor: "pointer" };
const statusPill = (status = "pending") => {
  const clean = String(status || "pending").toLowerCase();
  const color = clean === "won" ? "#15803d" : clean === "lost" ? "#b91c1c" : clean === "reverted" ? "#64748b" : "#b45309";
  const bg = clean === "won" ? "#dcfce7" : clean === "lost" ? "#fee2e2" : clean === "reverted" ? "#f1f5f9" : "#fef3c7";
  return { display: "inline-flex", padding: "6px 10px", borderRadius: "999px", background: bg, color, fontWeight: 900, textTransform: "capitalize" };
};
const emptyCell = { padding: "36px", textAlign: "center", color: "#94a3b8" };
const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 3000, display: "grid", placeItems: "center", padding: "16px" };
const modal = { width: "min(420px, 100%)", background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 24px 70px rgba(15,23,42,.28)", padding: "16px", display: "grid", gap: "12px" };
const modalHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", fontSize: "17px", fontWeight: 900 };
const closeButton = { width: 34, height: 34, border: 0, borderRadius: "999px", background: "#fee2e2", color: "#b91c1c", fontWeight: 900, cursor: "pointer" };
const modalMeta = { margin: 0, color: "#64748b", fontSize: "12px", fontWeight: 800 };
const field = { display: "grid", gap: "6px", color: "#334155", fontSize: "12px", fontWeight: 900 };
const input = { height: "44px", border: "1px solid #cbd5e1", borderRadius: "10px", padding: "0 12px", fontSize: "15px", outline: 0 };
const warningText = { margin: 0, padding: "10px 12px", borderRadius: "10px", background: "#fff7ed", color: "#9a3412", fontSize: "12px", fontWeight: 800 };
const modalFooter = { display: "flex", justifyContent: "flex-end", gap: "8px" };
const cancelButton = { height: "40px", border: "1px solid #cbd5e1", background: "#fff", borderRadius: "10px", padding: "0 14px", fontWeight: 800, cursor: "pointer" };
const saveButton = { height: "40px", border: 0, background: "#2563eb", color: "#fff", borderRadius: "10px", padding: "0 14px", fontWeight: 900, cursor: "pointer" };

export default AdminBidRevert;
